/**
 * Supabase client for the game web app.
 *
 * The native app passes a short-lived JWT via the URL:
 *   https://game.sivecta.com?token=<access_token>
 *
 * We initialise the client, then immediately set the session
 * using that token so all RPC calls run as the authenticated user.
 */

import { createClient } from '@supabase/supabase-js'

// These are public-facing values — safe to embed in a web build.
// In production, set via environment variables (VITE_ prefix).
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON || ''

if (!SUPABASE_URL || !SUPABASE_ANON) {
  console.warn('[supabase] Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON — check .env')
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON, {
  auth: {
    persistSession: false, // game session is ephemeral (JWT from URL)
    autoRefreshToken: false,
  },
})

/**
 * Initialise the Supabase session from the URL token.
 * Returns the user_hash (same identifier used in the native app).
 *
 * @returns {Promise<string|null>} user_hash or null on failure
 */
export async function initSession() {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')

  if (!token) {
    console.warn('[supabase] No token in URL — running in guest/dev mode')
    return null
  }

  try {
    // Set session — access_token only (no refresh_token needed for game session)
    const { data, error } = await supabase.auth.setSession({
      access_token: token,
      refresh_token: '',
    })

    if (error) throw error

    // user_hash = auth.uid() on the server, used as FK in game_islands
    const userId = data?.session?.user?.id ?? null
    return userId
  } catch (e) {
    console.error('[supabase] Session init failed:', e.message)
    return null
  }
}
